import { SearchProductOutputItem, useSearchProducts } from "@/lib/api/products"
import { useEffect, useState } from "react"
import { useTranslation } from 'react-i18next'
import { Input } from "../ui/input"
import { Loader2, Package, Search } from "lucide-react"
import { cn } from "@/lib/utils"
import { useDropdownPosition } from "@/hooks/use-dropdown-position"


interface SearchProductInputProps {
    storeId: string
    productId?: string
    onSelect: (product: SearchProductOutputItem) => void
}

const getProductImage = (product: SearchProductOutputItem): string | null => {
    const defaultVariant = product.variants.find((v) => v.default)
    return defaultVariant?.imageUrl ?? product.variants[0]?.imageUrl ?? null
}

export const SearchProductInput = ({ storeId, productId, onSelect }: SearchProductInputProps) => {
    const { t } = useTranslation('common')
    const [searchValue, setSearchValue] = useState<string | null>(null)
    const [focused, setFocused] = useState(false)
    const { data, isLoading } = useSearchProducts({
        storeId: storeId,
        productId: searchValue === null && productId ? productId : null,
        searchValue,
    })

    const showDropdown = focused && (isLoading || (data && data.length > 0) || false)
    const { containerRef, showAbove } = useDropdownPosition(showDropdown)

    useEffect(() => {
        if (productId && searchValue === null) {
            const foundProduct = data?.find((product) => product.id === productId)
            setSearchValue(foundProduct?.name ?? null)
        }
    }, [productId, data])

    const handleSelect = (product: SearchProductOutputItem) => {
        setSearchValue(product.name)
        setFocused(false)
        onSelect(product)
    }

    return (
        <div ref={containerRef} className="relative">
            <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                    value={searchValue ?? ''}
                    onChange={(e) => setSearchValue(e.target.value ?? '')}
                    placeholder={t('formComponents.searchProduct')}
                    onFocus={() => setFocused(true)}
                    onBlur={() => setTimeout(() => setFocused(false), 200)}
                    className="pl-10"
                />
            </div>
            {showDropdown && (
                <div className={cn(
                    "absolute z-50 w-full bg-background border rounded-lg shadow-lg max-h-[300px] overflow-y-auto",
                    showAbove ? "bottom-full mb-1" : "top-full mt-1"
                )}>
                    {isLoading && (
                        <div className="flex items-center justify-center p-4">
                            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                        </div>
                    )}
                    {!isLoading && data?.map((product) => {
                        const imageUrl = getProductImage(product)

                        return (
                            <div
                                key={product.id}
                                onClick={() => handleSelect(product)}
                                className={cn(
                                    "flex items-center gap-3 p-3 cursor-pointer transition-colors",
                                    "hover:bg-accent border-b last:border-b-0"
                                )}
                            >
                                <div className="flex-shrink-0 w-12 h-12 bg-muted rounded-md overflow-hidden flex items-center justify-center">
                                    {imageUrl ? (
                                        <img
                                            src={imageUrl}
                                            alt={product.name}
                                            className="w-full h-full object-cover"
                                        />
                                    ) : (
                                        <Package className="w-6 h-6 text-muted-foreground" />
                                    )}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="font-medium truncate">{product.name}</p>
                                    {product.description && (
                                        <p className="text-sm text-muted-foreground truncate">
                                            {product.description}
                                        </p>
                                    )}
                                </div>
                                <div className="flex-shrink-0 font-semibold text-sm">
                                    {product.variants.length}
                                </div>
                            </div>
                        )
                    })}
                </div>
            )}
        </div>
    )
}
